import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { ScreenContext } from "@foundation/screen";
import { trackClickEvent } from "@foundation/nextjs-gtm";
import { createClasses } from "@bloomberg/media-ui-core";
import { Change, Variant } from "@bloomberg/media-ui-markets";
import styles from "./styles.module.css";
import Heatmap from "~components/Heatmap";
import type {
  HeatmapTile,
  HeatmapTileData,
  TodayInMarketsItem,
} from "~types/types";
import { useTodayInMarkets } from "~context/TodayInMarketsContext";
import { getChangeSeverity, getTrend } from "~components/Heatmap/util";
import { addPageAction } from "~utils/newrelic/newrelicBrowser";
import { getCurrencySymbol } from "~utils/portfolio/getCurrencySymbol";
import { formatValue } from "~utils/section-front/formatChange";
import useLink from "~hooks/useLink";

const HEATMAP_DEFAULT_WIDTH = 350;
const HEATMAP_DEFAULT_HEIGHT = 326;
const HEATMAP_SMALL_DESKTOP_HEIGHT = 459;

export function TodayInMarketsHeatmap() {
  const { activeTicker, selectedCategory, updateActiveTicker } =
    useTodayInMarkets();

  const [width, setWidth] = useState(HEATMAP_DEFAULT_WIDTH);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const {
    breakpoints: { smallDesktop, largeDesktop },
  } = useContext(ScreenContext);

  const isDesktop = useMemo(
    () => Boolean(smallDesktop || largeDesktop),
    [smallDesktop, largeDesktop]
  );

  const height = useMemo(() => {
    if (largeDesktop) {
      return HEATMAP_DEFAULT_HEIGHT;
    }

    return smallDesktop ? HEATMAP_SMALL_DESKTOP_HEIGHT : HEATMAP_DEFAULT_HEIGHT;
  }, [smallDesktop, largeDesktop]);

  const tilesData: HeatmapTileData<TodayInMarketsItem>[] = useMemo(() => {
    if (!selectedCategory?.dataItems?.length) {
      return [];
    }

    return selectedCategory.dataItems.map((item) => {
      const percentChange = item.percentChange || 0;
      const severity = getChangeSeverity(percentChange);
      const trend = getTrend(percentChange);

      return {
        ...item,
        severity,
        trend,
        label: item.id,
        // all tiles are equal sized
        size: 1,
        marketData: {
          PX_LAST: item.price,
          CHG_PCT_1D: percentChange,
          CHG_NET_1D: item.priceChange1Day,
          CRNCY: item.currency,
        },
        shares: {
          number: 0,
        },
      };
    });
  }, [selectedCategory]);

  const link = useLink();

  // Calculate width for Heatmap
  useEffect(() => {
    // skip for larger screens. Use heatMapDefaultWidth
    if (smallDesktop || largeDesktop) {
      setWidth(HEATMAP_DEFAULT_WIDTH);

      return;
    }

    const observed = containerRef.current;

    if (!observed) return;

    // The heatMapComponent requires width as a prop.
    const observer = new ResizeObserver((entries) => {
      // ResizeObserver's callback is called when the target element changes in size
      // The callback returns an array entries, it contains metadata
      // The width is obtained from the metadata array
      for (const entry of entries) {
        const nextWidth =
          entry.contentBoxSize?.[0]?.inlineSize || entry.contentRect.width;

        if (nextWidth === 0) return;
        setWidth(nextWidth);
      }
    });

    observer.observe(observed);

    // eslint-disable-next-line consistent-return
    return () => {
      observer.disconnect();
    };
  }, [smallDesktop, largeDesktop]);

  const onTileClick = useCallback((tile: HeatmapTile<TodayInMarketsItem>) => {
    trackClickEvent("Market_Data", "heatmap_click_tim", "click", "Market Data");

    addPageAction("todayInMarketsHeatmapClick", {
      ticker: tile.data.id,
    });

    // open quotes page on click
    link.open(`/quote/${tile.data.id}`, "_blank");
  }, []);

  const onTileFocus = useCallback(
    (tile: HeatmapTile<TodayInMarketsItem>) => {
      trackClickEvent(
        "Market_Data",
        "heatmap_hover_tim",
        "click",
        "Market Data"
      );

      updateActiveTicker(tile.data.id);
    },
    [updateActiveTicker]
  );

  const onTileBlur = useCallback(
    (_: HeatmapTile<TodayInMarketsItem>) => {
      updateActiveTicker(undefined);
    },
    [updateActiveTicker]
  );

  const renderHoverContent = (tile: HeatmapTile<TodayInMarketsItem>) => {
    return (
      <>
        <div
          className={createClasses(styles.hoverContent, styles.tickerSymbol)}
        >
          {tile.data.id}
        </div>

        <div className={styles.hoverContent}>
          {getCurrencySymbol(tile.data.currency || "")}{" "}
          {formatValue(tile.data.price)}
        </div>

        {/* render change when market data is available */}
        {Number.isFinite(tile.data.percentChange) &&
          Number.isFinite(tile.data.priceChange1Day) && (
            <div className={styles.hoverContent}>
              <Change
                name="change"
                className="pb-0.5"
                percentChange={tile.data.percentChange}
                priceChange={tile.data.priceChange1Day}
                size="large"
                variant={Variant.refresh2024}
              />
            </div>
          )}
      </>
    );
  };

  return (
    <div ref={containerRef} className={styles.todayInMarketsHeatmapContainer}>
      <Heatmap<TodayInMarketsItem>
        width={width}
        height={height}
        tilesData={tilesData}
        activeTileLabel={activeTicker}
        onTileClick={onTileClick}
        onTileFocus={onTileFocus}
        onTileBlur={onTileBlur}
        enableTileFocusEvents={isDesktop}
        renderHoverContent={renderHoverContent}
      />
    </div>
  );
}
